import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPost } from '@/lib/api'
import { ROTAS_DE_IA } from '@/lib/endpoints'

/**
 * A camada de IA no painel — a camada de dados.
 *
 * ⚠️ ESTA TELA NUNCA QUEBRA POR CAUSA DA IA. A API devolve 200 com
 * `disponivel: false` quando a camada está desligada, quando o teto do dia
 * estourou e quando a chamada falhou — e o componente mostra "insight
 * indisponível" com o motivo. Nenhum NÚMERO do painel vem daqui: os números
 * vêm do BI, calculado sem IA nenhuma (invariante nº 3).
 *
 * ⚠️ `retry: false` em tudo. O padrão do TanStack Query é retentar, e aqui isso
 * é errado nos dois sentidos: a resposta é 200 mesmo quando indisponível (não
 * há o que retentar), e uma geração sob demanda que falhou não deve ser
 * repetida sozinha — cada tentativa custa dinheiro.
 */

export const TIPOS_DE_INSIGHT = [
  'BRIEFING_DIARIO',
  'EXPLICACAO_DE_ANOMALIA',
  'RECOMENDACAO_DE_FROTA',
  'SUGESTAO_DE_PRECO_SAZONAL',
] as const

export type TipoDeInsight = (typeof TIPOS_DE_INSIGHT)[number]

export const fonteSchema = z.object({
  caminho: z.string(),
  valor: z.string(),
})

export const itemDoInsightSchema = z.object({
  titulo: z.string(),
  texto: z.string(),
  fontes: z.array(fonteSchema),
  urgencia: z.enum(['ALTA', 'MEDIA', 'BAIXA']).optional(),
  forca: z.enum(['FORTE', 'MODERADA', 'FRACA']).optional(),
  confianca: z.enum(['ALTA', 'MEDIA', 'BAIXA']).optional(),
  categoria: z.string().optional(),
  acao: z.enum(['AUMENTAR', 'REDUZIR', 'MANTER']).optional(),
  semana: z.object({ inicio: z.string(), fim: z.string(), rotulo: z.string() }).optional(),
  multiplicadorEmPontosBase: z.number().int().optional(),
})

export const insightSchema = z.object({
  disponivel: z.boolean(),
  tipo: z.enum(TIPOS_DE_INSIGHT),
  motivo: z.string().nullable(),
  referencia: z.string().nullable(),
  geradoEm: z.string().nullable(),
  geradoPorIa: z.literal(true),
  modelo: z.string().nullable(),
  versaoDoPrompt: z.string().nullable(),
  insightId: z.string().nullable(),
  itens: z.array(itemDoInsightSchema),
  rodape: z.string().nullable(),
  itensDescartados: z.number().int(),
})

export type Insight = z.infer<typeof insightSchema>
export type ItemDoInsight = z.infer<typeof itemDoInsightSchema>

export const consumoDeIaSchema = z.object({
  dia: z.string(),
  tokens: z.number().int(),
  chamadas: z.number().int(),
  tetoDeTokens: z.number().int(),
  custoEmDolar: z.string().nullable(),
})

export type ConsumoDeIa = z.infer<typeof consumoDeIaSchema>

export const chavesDeIa = {
  insight: (tipo: TipoDeInsight) => ['ia', 'insight', tipo] as const,
  consumo: ['ia', 'consumo'] as const,
}

export function useInsight(tipo: TipoDeInsight, habilitado = true): UseQueryResult<Insight> {
  return useQuery({
    queryKey: chavesDeIa.insight(tipo),
    queryFn: () => apiGet(ROTAS_DE_IA.insight(tipo), insightSchema),
    enabled: habilitado,
    retry: false,
    // O insight muda uma vez por noite. Refazer a busca a cada foco de janela
    // seria tráfego para reconfirmar o mesmo texto.
    staleTime: 5 * 60_000,
  })
}

export function useConsumoDeIa(habilitado = true): UseQueryResult<ConsumoDeIa> {
  return useQuery({
    queryKey: chavesDeIa.consumo,
    queryFn: () => apiGet(ROTAS_DE_IA.consumo, consumoDeIaSchema),
    enabled: habilitado,
    retry: false,
    staleTime: 60_000,
  })
}

export function useGerarInsight(tipo: TipoDeInsight) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost(ROTAS_DE_IA.gerar(tipo), insightSchema, {}),
    retry: false,
    onSuccess: (insight) => {
      cliente.setQueryData(chavesDeIa.insight(tipo), insight)
      // O consumo mudou, e a mesma tela mostra o teto do dia.
      void cliente.invalidateQueries({ queryKey: chavesDeIa.consumo })
    },
  })
}

export function useVotarNoItem(tipo: TipoDeInsight) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (voto: { insightId: string; itemIndice: number; util: boolean }) =>
      apiPost(ROTAS_DE_IA.feedback(voto.insightId), z.unknown(), {
        itemIndice: voto.itemIndice,
        util: voto.util,
      }),
    retry: false,
    onSettled: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeIa.insight(tipo) })
    },
  })
}
